import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaMicrophone, FaPaperPlane, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { sendChatMessage, QUICK_ACTIONS } from '../../api/assistantService';
import './Assistant.scss';

const Assistant = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // İlk açılışta karşılama mesajı
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'Merhaba! Size nasıl yardımcı olabilirim? İstediğiniz konuyu sorabilirsiniz.',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Speech Recognition (STT)
  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Tarayıcınız ses tanıma özelliğini desteklemiyor.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'tr-TR';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
      console.log('[STT] Listening started');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      console.log('[STT] Transcript:', transcript);
      setInputMessage(transcript);
      setIsListening(false);
      recognition.stop();
    };

    recognition.onerror = (event) => {
      console.error('[STT] Error:', event.error);
      setIsListening(false);
      if (event.error === 'no-speech') {
        alert('Ses duymadım, tekrar deneyin.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      console.log('[STT] Listening ended');
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  // Text to Speech (TTS)
  const speakText = (text) => {
    if (!('speechSynthesis' in window)) {
      console.warn('[TTS] Not supported');
      return;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'tr-TR';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    utterance.onstart = () => {
      setIsSpeaking(true);
      console.log('[TTS] Speaking started');
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      console.log('[TTS] Speaking ended');
    };

    utterance.onerror = (event) => {
      setIsSpeaking(false);
      console.error('[TTS] Error:', event);
    };

    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  // Mesaj gönder
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Kullanıcı mesajını ekle
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      // Conversation history hazırla (son 5 mesaj)
      const conversationHistory = messages
        .slice(-5)
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));

      const response = await sendChatMessage(userMessage, conversationHistory, {
        site: 'https://iskenderun.bel.tr',
        locale: 'tr-TR',
        voiceEnabled: true,
        screen: window.innerWidth > 768 ? 'desktop' : 'mobile'
      });

      console.log('[ASSISTANT] Full response:', response);

      if (response?.isSuccess && response?.data) {
        let replyContent = response.data.reply || 'Üzgünüm, yanıt oluşturamadım.';
        
        // DEBUG mesajlarını ayır ve console'a logla
        const debugMatch = replyContent.match(/\[DEBUG:([^\]]+)\]/);
        if (debugMatch) {
          const debugMessage = debugMatch[1].trim();
          console.warn('[ASSISTANT DEBUG]', debugMessage);
          console.log('[ASSISTANT] Full response:', response);
          
          // DEBUG mesajını kullanıcıya gösterme
          replyContent = replyContent.replace(/\[DEBUG:[^\]]+\]\s*/g, '').trim();
          
          // Eğer sadece DEBUG mesajı varsa, kullanıcıya bilgi ver
          if (!replyContent || replyContent.length === 0) {
            replyContent = 'Üzgünüm, şu anda yanıt oluşturamadım. Lütfen tekrar deneyin.';
            console.error('[ASSISTANT] Sadece DEBUG mesajı geldi, kullanıcıya genel mesaj gösteriliyor');
          }
        } else {
          // DEBUG mesajı yok - Gemini API başarılı demektir
          console.log('[ASSISTANT] ✅ Gemini API yanıtı başarıyla alındı (DEBUG mesajı yok)');
          console.log('[ASSISTANT] Reply içeriği (tam):', replyContent);
          console.log('[ASSISTANT] Reply uzunluğu:', replyContent.length);
          console.log('[ASSISTANT] Reply başlangıcı:', replyContent.substring(0, 200));
          
          // Eğer yanıt hala genel mesaj gibi görünüyorsa, uyarı ver
          if (replyContent.includes('Size nasıl yardımcı olabilirim') && 
              replyContent.includes('Borç ödeme işlemleri')) {
            console.warn('[ASSISTANT] ⚠️ Yanıt hala genel mesaj gibi görünüyor. Gemini system prompt\'u düzgün uygulamıyor olabilir.');
            console.warn('[ASSISTANT] Backend console\'da Gemini API request/response loglarını kontrol edin.');
          }
        }
        
        const links = response.data.links || [];
        
        // Eğer tek bir link varsa, otomatik yönlendir
        if (links.length === 1) {
          const link = links[0];
          const url = new URL(link.url);
          
          // Mesajı göster (URL'yi çıkar)
          let displayMessage = replyContent.replace(/https?:\/\/[^\s]+/g, '').trim();
          if (!displayMessage || displayMessage.length === 0) {
            displayMessage = 'Yönlendiriliyorum...';
          }
          
          const assistantMessage = {
            role: 'assistant',
            content: displayMessage,
            links: links,
            timestamp: new Date()
          };

          setMessages(prev => [...prev, assistantMessage]);
          
          // 1 saniye sonra otomatik yönlendir
          setTimeout(() => {
            navigate(url.pathname);
          }, 1000);
        } else {
          // Normal mesaj göster
          const assistantMessage = {
            role: 'assistant',
            content: replyContent,
            links: links,
            timestamp: new Date()
          };

          setMessages(prev => [...prev, assistantMessage]);

          // TTS varsa oku (DEBUG mesajı olmadan)
          if (response.data.speak && replyContent) {
            speakText(replyContent);
          }
        }
      } else {
        console.error('[ASSISTANT] API Error:', response);
        throw new Error(response?.resultMessage || 'Yanıt alınamadı');
      }
    } catch (error) {
      console.error('[ASSISTANT] Error:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: 'Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin.',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Hızlı işlem
  const handleQuickAction = (action) => {
    if (action.action === 'modal' && action.id === 'eczane') {
      // Nöbetçi eczane için modal aç
      // Önce ana sayfaya git
      if (window.location.pathname !== '/') {
        navigate('/');
        // Sayfa yüklendikten sonra modal'ı aç
        setTimeout(() => {
          const eczaneButton = document.querySelector(
            '[href*="nobetci-eczane"], [onclick*="eczane"], button[aria-label*="eczane"], button[aria-label*="Eczane"], a[href*="eczane"]'
          );
          if (eczaneButton) {
            eczaneButton.click();
          } else {
            // Modal bulunamazsa sayfaya yönlendir
            navigate('/nobetci-eczane');
          }
        }, 500);
      } else {
        // Zaten ana sayfadaysak direkt modal'ı aç
        const eczaneButton = document.querySelector(
          '[href*="nobetci-eczane"], [onclick*="eczane"], button[aria-label*="eczane"], button[aria-label*="Eczane"], a[href*="eczane"]'
        );
        if (eczaneButton) {
          eczaneButton.click();
        } else {
          navigate('/nobetci-eczane');
        }
      }
    } else if (action.url) {
      // Diğer butonlar için sayfaya yönlendir (React Router kullan)
      navigate(action.url);
    } else {
      // Fallback: Eski davranış (mesaj gönder)
      setInputMessage(action.title);
      setTimeout(() => handleSendMessage(), 100);
    }
  };

  // Enter to send
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="assistant-container">
      {/* Chat Window */}
      {isOpen && (
        <div className="assistant-window">
          {/* Header */}
          <div className="assistant-header">
            <h3>İskenderun Belediyesi Asistanı</h3>
            <button
              className="close-button"
              onClick={() => setIsOpen(false)}
              aria-label="Kapat"
            >
              <FaTimes />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action.id}
                className="quick-action-btn"
                onClick={() => handleQuickAction(action)}
                disabled={isLoading}
                title={action.title}
              >
                <span className="action-icon">{action.icon}</span>
                <span className="action-title">{action.title}</span>
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="assistant-messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
              >
                <div className="message-content">
                  <p>{msg.content}</p>
                  {msg.links && msg.links.length > 0 && (
                    <div className="message-links">
                      {msg.links.map((link, i) => (
                        <a
                          key={i}
                          href={link.url}
                          onClick={(e) => {
                            e.preventDefault();
                            // React Router ile yönlendir
                            const url = new URL(link.url);
                            navigate(url.pathname);
                          }}
                          className="message-link"
                        >
                          {link.title} →
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <span className="message-time">
                  {msg.timestamp?.toLocaleTimeString('tr-TR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
            ))}
            {isLoading && (
              <div className="message assistant-message">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="assistant-input">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Sorunuzu yazın veya mikrofona basın..."
              disabled={isLoading}
              rows={1}
            />
            <div className="input-actions">
              <button
                className={`action-btn ${isListening ? 'active' : ''}`}
                onClick={isListening ? stopListening : startListening}
                disabled={isLoading}
                aria-label="Ses Tanıma"
              >
                <FaMicrophone />
              </button>
              <button
                className={`action-btn ${isSpeaking ? 'active' : ''}`}
                onClick={isSpeaking ? stopSpeaking : () => {}}
                disabled={isLoading || !isSpeaking}
                aria-label="Sesli Okuma"
              >
                {isSpeaking ? <FaVolumeMute /> : <FaVolumeUp />}
              </button>
              <button
                className="send-btn"
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                aria-label="Gönder"
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assistant;
